import React from "react";

/**
 * StageControls — the controls layer of the stage (Plan G). Shown or hidden
 * by the stage (useAutoHide); every control calls onPoke so a tap keeps
 * them up for another beat. Nothing here owns playback state.
 *
 * Props: counter (text, top-left), playing, onPlay, onPause, onClose,
 * scenes/currentIdx/onSeek (dots; omitted in single-scene mode), progress
 * 0–1 within the current scene, timeText, prevLabel/nextLabel + onPrev/onNext
 * (omitted in single-scene mode), visible, onPoke.
 */
function Glyph({ kind }) {
  if (kind === "pause") return <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>;
  return <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 4v16l13-8z"/></svg>;
}

export default function StageControls({
  counter, playing, onPlay, onPause, onClose,
  scenes = [], currentIdx = 0, onSeek, progress = 0, timeText = "",
  prevLabel, nextLabel, onPrev, onNext, single = false,
  visible = true, onPoke, compact = true,
}) {
  const poke = fn => e => { e.stopPropagation(); onPoke?.(); fn?.(); };
  const vis = { opacity: visible ? 1 : 0, pointerEvents: visible ? "auto" : "none", transition: "opacity 220ms ease" };
  return (
    <>
      <div style={{ ...c.top, ...vis }}>
        <span style={c.counter}>{counter}</span>
        <button type="button" aria-label="Close" onClick={poke(onClose)} style={c.close}>×</button>
      </div>

      <div style={{ ...c.centre, ...vis }}>
        <button type="button" aria-label={playing ? "Pause" : "Play"} onClick={poke(playing ? onPause : onPlay)} style={c.playBtn}>
          <Glyph kind={playing ? "pause" : "play"} />
        </button>
      </div>

      <div style={{ ...c.bottom, ...(compact ? {} : c.bottomWide), ...vis }}>
        <div style={c.dotsRow}>
          {!single && scenes.map((sc, i) => {
            const on = i === currentIdx;
            return (
              <button key={sc.scene_id || i} type="button" aria-label={`Go to ${sc.title || `scene ${i + 1}`}`}
                      onClick={poke(() => onSeek?.(i))}
                      style={{ ...c.dot, ...(i < currentIdx ? c.dotDone : {}), ...(on ? c.dotOn : {}) }}>
                {on && <span style={{ ...c.dotFill, width: `${Math.round(progress * 100)}%` }} />}
              </button>
            );
          })}
          {single && <span style={{ ...c.dot, ...c.dotOn, flex: 1 }}><span style={{ ...c.dotFill, width: `${Math.round(progress * 100)}%` }} /></span>}
          <span style={c.time}>{timeText}</span>
        </div>
        {!single && (
          <div style={c.navRow}>
            <button type="button" aria-label="Previous scene" onClick={poke(onPrev)} disabled={!onPrev} style={{ ...c.navBtn, visibility: onPrev ? "visible" : "hidden" }}>‹ {prevLabel || "Back"}</button>
            <button type="button" aria-label="Next scene" onClick={poke(onNext)} disabled={!onNext} style={{ ...c.navBtn, visibility: onNext ? "visible" : "hidden" }}>{nextLabel || "Next"} ›</button>
          </div>
        )}
      </div>
    </>
  );
}

const c = {
  top:      { position: "absolute", left: 16, right: 16, top: "max(16px, env(safe-area-inset-top))", display: "flex", alignItems: "center", justifyContent: "space-between", zIndex: 6 },
  counter:  { fontSize: 13, color: "var(--overlay-text-muted)", letterSpacing: "0.04em" },
  close:    { width: 44, height: 44, borderRadius: 999, border: "none", background: "var(--overlay-chip-bg)", color: "var(--overlay-text)", fontSize: 24, lineHeight: 1, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", font: "inherit" },
  centre:   { position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 5, pointerEvents: "none" },
  playBtn:  { pointerEvents: "auto", width: 72, height: 72, borderRadius: 999, border: "none", background: "rgba(26,25,21,0.55)", color: "var(--overlay-text)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" },
  bottom:   { position: "absolute", left: 20, right: 20, bottom: "max(28px, env(safe-area-inset-bottom))", display: "flex", flexDirection: "column", gap: 14, zIndex: 6 },
  bottomWide:{ left: 48, right: 48 },
  dotsRow:  { display: "flex", alignItems: "center", gap: 6 },
  dot:      { flex: "1 1 0", maxWidth: 28, height: 4, borderRadius: 2, background: "rgba(255,255,255,0.35)", border: "none", padding: 0, position: "relative", overflow: "hidden", cursor: "pointer" },
  dotDone:  { background: "rgba(255,255,255,0.9)" },
  dotOn:    { flex: "2 1 0", maxWidth: 64 },
  dotFill:  { position: "absolute", left: 0, top: 0, bottom: 0, background: "#fff" },
  time:     { marginLeft: "auto", fontSize: 12, color: "var(--overlay-text-muted)", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" },
  navRow:   { display: "flex", alignItems: "center", justifyContent: "space-between" },
  navBtn:   { font: "inherit", height: 44, padding: "0 16px", borderRadius: 999, border: "1px solid var(--overlay-chip-border)", background: "rgba(26,25,21,0.35)", color: "var(--overlay-text)", fontSize: 15, cursor: "pointer", maxWidth: "46%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
};
